import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ImportApiResponse, ImportRequest, ImportApiError } from '../interfaces/import-response.interface';
import { ImportError } from '../models/import-error.model';

export interface ImportProgress {
  stage: 'uploading' | 'processing' | 'validating' | 'completed' | 'error';
  percentage: number;
  message: string;
  currentRow?: number;
  totalRows?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImportApiService {
  private progressSubject = new BehaviorSubject<ImportProgress | null>(null);
  public progress$ = this.progressSubject.asObservable();

  constructor(private http: HttpClient) {}

  async executeImport<T = any>(
    endpoint: string, 
    data: any[], 
    metadata?: any,
    customHandler?: (data: any[], metadata?: any) => Promise<ImportApiResponse<T>>
  ): Promise<ImportApiResponse<T>> {
    try {
      this.updateProgress('uploading', 0, 'Iniciando importación...');
      
      // Use custom handler if provided
      if (customHandler) {
        this.updateProgress('processing', 25, 'Procesando...');
        const response = await customHandler(data, metadata);
        this.updateProgress('completed', 100, 'Importación completada');
        return response;
      }
      
      // Default HTTP implementation
      const request: ImportRequest = {
        data,
        metadata
      };

      this.updateProgress('processing', 25, 'Enviando datos...');

      const response = await this.http.post<ImportApiResponse<T>>(endpoint, request)
        .pipe(
          map(response => this.processApiResponse(response)),
          catchError(error => this.handleApiError(error))
        ).toPromise();

      this.updateProgress('completed', 100, 'Importación completada');
      
      return response!;
    } catch (error) {
      this.updateProgress('error', 0, 'Error durante la importación');
      throw error;
    }
  }

  private processApiResponse<T>(response: ImportApiResponse<T>): ImportApiResponse<T> {
    if (!response.success && response.errors) {
      this.updateProgress('validating', 75, `Procesando errores: ${response.errors.length} encontrados`);
    } else {
      this.updateProgress('processing', 50, 'Validando datos...');
    }

    return {
      ...response,
      errors: response.errors?.map(error => this.normalizeApiError(error)) || []
    };
  }

  private normalizeApiError(error: any): ImportApiError {
    return {
      row: error.row || error.rowIndex || 0,
      column: error.column || error.field || 'general',
      field: error.field || error.column,
      message: error.message || 'Error desconocido',
      code: error.code,
      severity: error.severity || 'error'
    };
  }

  private handleApiError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Error desconocido durante la importación';
    let apiErrors: ImportApiError[] = [];

    if (error.error) {
      if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      }

      if (error.error.errors && Array.isArray(error.error.errors)) {
        apiErrors = error.error.errors.map(this.normalizeApiError);
      }
    }

    const errorResponse: ImportApiResponse = {
      success: false,
      errors: apiErrors,
      totalProcessed: 0,
      totalSuccess: 0,
      totalFailed: 0,
      message: errorMessage
    };

    return throwError(errorResponse);
  }

  validateDataOnServer(endpoint: string, data: any[]): Observable<ImportApiResponse> {
    const validateEndpoint = endpoint.replace('/import', '/validate') + '/validate';
    
    this.updateProgress('validating', 0, 'Validando datos en el servidor...');
    
    return this.http.post<ImportApiResponse>(validateEndpoint, { data })
      .pipe(
        map(response => {
          this.updateProgress('validating', 100, 'Validación completada');
          return this.processApiResponse(response);
        }),
        catchError(error => {
          this.updateProgress('error', 0, 'Error en validación del servidor');
          return this.handleApiError(error);
        })
      );
  }

  uploadFileForPreProcessing(endpoint: string, file: File): Observable<ImportApiResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    this.updateProgress('uploading', 0, 'Subiendo archivo...');
    
    return this.http.post<ImportApiResponse>(endpoint, formData)
      .pipe(
        map(response => {
          this.updateProgress('processing', 50, 'Archivo procesado');
          return this.processApiResponse(response);
        }),
        catchError(error => {
          this.updateProgress('error', 0, 'Error subiendo archivo');
          return this.handleApiError(error);
        })
      );
  }

  mapApiErrorsToImportErrors(apiErrors: ImportApiError[]): ImportError[] {
    return apiErrors.map(apiError => 
      ImportError.fromApiError(apiError, apiError.row)
    );
  }

  private updateProgress(stage: ImportProgress['stage'], percentage: number, message: string, currentRow?: number, totalRows?: number): void {
    this.progressSubject.next({
      stage,
      percentage,
      message,
      currentRow,
      totalRows
    });
  }

  clearProgress(): void {
    this.progressSubject.next(null);
  }

  createImportMetadata(fileName: string, sheetName: string, data: any[], columns: string[]): any {
    return {
      fileName,
      sheetName,
      totalRows: data.length,
      columns,
      timestamp: new Date(),
      userAgent: navigator.userAgent,
      clientVersion: '1.0.0'
    };
  }

  formatApiError(error: ImportApiError): string {
    const rowText = error.row ? `Fila ${error.row + 1}` : 'Error general';
    const columnText = error.column && error.column !== 'general' ? `, ${error.column}` : '';
    return `${rowText}${columnText}: ${error.message}`;
  }

  groupErrorsByRow(errors: ImportApiError[]): { [row: number]: ImportApiError[] } {
    return errors.reduce((acc, error) => {
      const row = error.row || 0;
      if (!acc[row]) {
        acc[row] = [];
      }
      acc[row].push(error);
      return acc;
    }, {} as { [row: number]: ImportApiError[] });
  }

  getErrorSummary(response: ImportApiResponse): string {
    if (response.success) {
      return `Importación exitosa: ${response.totalSuccess} de ${response.totalProcessed} registros procesados.`;
    }
    
    const errorCount = response.errors?.length || 0;
    const failedCount = response.totalFailed || 0;
    
    return `Importación con errores: ${response.totalSuccess} exitosos, ${failedCount} fallidos. ${errorCount} errores encontrados.`;
  }
}